// TrackFiji V3.3.0 / V3.9.0 (seconds, hidden devices) — live "tails": a fading trail behind each marker on the live map.
// Seeded from /api/tails (Traccar route, cleaned with the GPS-quality rules), then extended from realtime updates.
import { useEffect, useRef } from "react";
import type { GeoJSONSource, Map as MLMap } from "maplibre-gl";
import { supabase, type Device } from "../../lib/supabase";
import { onStyleReady } from "../../lib/mapready";
import { clipTail, tailSec } from "../../lib/tail";

type Pt = [number, number, number]; // lng, lat, epoch ms
const DEFAULT_COLOUR = "#257AF4";

export function useTails(map: MLMap | null, devices: Device[], visibleIds: Set<string>, now: number, hidden: boolean) {
  const tails = useRef(new Map<string, Pt[]>());
  const seededKey = useRef("");

  const on = devices.filter((d) => d.tail_on !== false && d.state?.lat != null);
  const key = on.map((d) => `${d.id}:${tailSec(d)}`).sort().join(",");

  // seed (and re-seed when the set of tailed devices or their lengths change)
  useEffect(() => {
    if (!on.length || key === seededKey.current) return;
    seededKey.current = key;
    // fetch a little more than the window so the first point can be interpolated at the cut-off
    const seconds = Math.min(14400, Math.max(...on.map((d) => tailSec(d))) + 120);
    let live = true;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const r = await fetch(`/api/tails?seconds=${seconds}&devices=${on.map((d) => d.id).join(",")}`,
          { headers: { Authorization: `Bearer ${data.session?.access_token ?? ""}` } });
        if (!r.ok || !live) return;
        const j = (await r.json()) as { tails: Record<string, Pt[]> };
        for (const [id, pts] of Object.entries(j.tails ?? {})) {
          const cur = tails.current.get(id) ?? [];
          const merged = [...pts, ...cur.filter((p) => p[2] > (pts[pts.length - 1]?.[2] ?? 0))];
          tails.current.set(id, merged);
        }
        draw();
      } catch { /* tails are cosmetic — ignore */ }
    })();
    return () => { live = false; };
  }, [key]); // eslint-disable-line

  // extend from live positions (only confirmed, good/low fixes move the tail)
  useEffect(() => {
    for (const d of on) {
      const s = d.state!;
      if (s.quality === "no_fix" || s.quality === "suspect" || s.lat == null || s.lng == null) continue;
      const t = Date.parse(s.fix_at ?? "");
      if (!Number.isFinite(t)) continue;
      const arr = tails.current.get(d.id) ?? [];
      const last = arr[arr.length - 1];
      if (!last || t > last[2]) { arr.push([s.lng, s.lat, t]); tails.current.set(d.id, arr); }
    }
    draw();
  }, [devices, now, hidden, map, visibleIds]); // eslint-disable-line

  function draw() {
    if (!map) return;
    const ready = () => {
      const features: any[] = [];
      const cutoffNow = Date.now();
      for (const d of on) {
        if (!visibleIds.has(d.id)) continue;
        const keepFrom = cutoffNow - tailSec(d) * 1000;
        const all = tails.current.get(d.id) ?? [];
        // keep one point before the window (needed to interpolate the start), drop the rest
        const firstIn = all.findIndex((p) => p[2] >= keepFrom);
        tails.current.set(d.id, firstIn > 0 ? all.slice(firstIn - 1) : firstIn < 0 ? all.slice(-1) : all);
        const pts = clipTail(all, keepFrom);
        if (pts.length < 2) continue;
        const colour = d.tail_color ?? d.icon_color ?? DEFAULT_COLOUR;
        for (let i = 1; i < pts.length; i++) {
          const age = (pts[i][2] - keepFrom) / (cutoffNow - keepFrom); // 0 = oldest, 1 = newest
          features.push({ type: "Feature", properties: { c: colour, o: 0.12 + 0.78 * Math.max(0, Math.min(1, age)) },
            geometry: { type: "LineString", coordinates: [[pts[i - 1][0], pts[i - 1][1]], [pts[i][0], pts[i][1]]] } });
        }
      }
      const data = { type: "FeatureCollection" as const, features };
      const src = map.getSource("tf-tails") as GeoJSONSource | undefined;
      if (src) src.setData(data);
      else {
        map.addSource("tf-tails", { type: "geojson", data });
        map.addLayer({ id: "tf-tails", type: "line", source: "tf-tails", layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": ["get", "c"], "line-opacity": ["get", "o"], "line-width": ["interpolate", ["linear"], ["zoom"], 8, 2, 14, 4, 18, 6] } });
      }
      map.setLayoutProperty("tf-tails", "visibility", hidden ? "none" : "visible");
    };
    onStyleReady(map, ready, "tails");
  }
}
